/**
 * InlineSelect Component
 * Compact single-value select rendered inline in the filter bar (no popover).
 * Applies on every change so filters take effect live.
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import type { InlineSelectOption } from './InlineMultiSelect';

export interface InlineSelectProps {
  label: string;
  value: string;
  options: InlineSelectOption[];
  onChange: (value: string) => void;
  /** Renders a leading "all" option with an empty value. */
  emptyLabel?: string;
  minWidth?: number;
  maxWidth?: number;
  disabled?: boolean;
}

export const InlineSelect: React.FC<InlineSelectProps> = ({
  label,
  value,
  options,
  onChange,
  emptyLabel,
  minWidth = 150,
  maxWidth = 240,
  disabled = false,
}) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  // With an "all" option the field is never truly empty (it shows emptyLabel),
  // so the label must stay floated and the outline notched — otherwise the label
  // overlaps the displayed value.
  const hasEmptyOption = emptyLabel !== undefined;

  return (
    <FormControl size="small" sx={{ minWidth, maxWidth }}>
      <InputLabel shrink={hasEmptyOption || undefined}>{label}</InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        disabled={disabled}
        input={<OutlinedInput label={label} notched={hasEmptyOption || undefined} />}
        displayEmpty={hasEmptyOption}
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      >
        {emptyLabel !== undefined && (
          <MenuItem value="">
            <em>{emptyLabel}</em>
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
